/**
 * Maps app routes to the minimum role required. Align with auth-wizard JWT /me `role`
 * (`user_mode`, `manager_mode`, `sudo_mode`).
 */

export const APP_ROLES = ['user_mode', 'manager_mode', 'sudo_mode'] as const;
export type AppRole = (typeof APP_ROLES)[number];

const ROLE_RANK: Record<AppRole, number> = {
  user_mode: 0,
  manager_mode: 1,
  sudo_mode: 2,
};

/** Back-compat if JWT or /me still emit legacy labels. */
const LEGACY_ROLE_MAP: Record<string, AppRole> = {
  admin: 'sudo_mode',
  sudo: 'sudo_mode',
  manager: 'manager_mode',
  user: 'user_mode',
};

/** Route `path` values under the authenticated layout (see AppRoutes). */
export const ROUTE_MIN_ROLE = {
  'floor-plan': 'manager_mode',
  containers: 'user_mode',
  analytics: 'user_mode',
  tasks: 'manager_mode',
  disables: 'manager_mode',
  'container-types': 'manager_mode',
  users: 'manager_mode',
  packages: 'user_mode',
  settings: 'user_mode',
  customers: 'sudo_mode',
} as const satisfies Record<string, AppRole>;

export type ProtectedAppRoute = keyof typeof ROUTE_MIN_ROLE;

export function normalizeAppRole(role: string | undefined): AppRole | null {
  if (!role) {
    return null;
  }
  const trimmed = role.trim();
  if ((APP_ROLES as readonly string[]).includes(trimmed)) {
    return trimmed as AppRole;
  }
  return LEGACY_ROLE_MAP[trimmed.toLowerCase()] ?? null;
}

/** True if `userRole` is at least `minimum` in the sudo > manager > user ladder. */
export function roleHasMinimum(userRole: string | undefined, minimum: AppRole): boolean {
  const role = normalizeAppRole(userRole);
  if (role === null) {
    return false;
  }
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export function minimumRoleForRoute(routePath: ProtectedAppRoute): AppRole {
  return ROUTE_MIN_ROLE[routePath];
}
